// src/pages/DashboardPage.tsx
//
// Every tile here is either a REAL count computed from `/api/bills/status-counts`,
// `/api/bills` and `/api/audit` (never a sum of money — counts only, per the
// money rule), or an explicit "not built yet" tile for a measure no tender clause
// defines (ux-direction.md §4.2 — "Part 2, Section B" is referenced but not in
// the pack, so no clause defines a single PRS2 dashboard metric).
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { StatusBarChart } from '../components/StatusBarChart'
import { Sparkline } from '../components/charts/Sparkline'
import { TrendChart, type TrendSeries } from '../components/charts/TrendChart'
import { StackedColumns } from '../components/charts/StackedColumns'
import { DonutChart } from '../components/charts/DonutChart'
import { RankedBars } from '../components/charts/RankedBars'
import { ActivityHeatmap } from '../components/charts/ActivityHeatmap'
import { ProgressRing } from '../components/charts/ProgressRing'
import { BILL_STATUSES } from '../lib/types'
import { SgdsButton } from '@govtechsg/sgds-web-component/react'
import { mayAdministerReferenceData, ROLE_LABELS } from '@prs2/auth'
import { api } from '../lib/api'
import { useApi, type ApiFetchState } from '../lib/useApi'
import { useSort, sortParams } from '../lib/useSort'
import { useAuth } from '../state/AuthContext'
import { canActOn } from '../lib/authority'
import { breakdownOf } from '../lib/status-breakdown'
import { ActionBadge, AuditOutcomeCell, BillStatusBadge, BillTypeBadge, RoleBadge } from '../components/StatusBadge'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { AwaitingCard, EmptyCard, ErrorCard, LoadingCard, NoPermissionCard } from '../components/StateCards'
import { BILL_TYPE_LABELS, BILL_TYPE_SHORT, formatInstant, formatIsoDate } from '../lib/format'
import type { Bill, DashboardSeriesResponse, InterfaceAlertCountsResponse } from '../lib/types'

/**
 * The 4 columns this preview renders, out of `AuditPage`'s 6 sortable ones —
 * both are keys of `AUDIT_SORT_COLUMNS` (`packages/persistence/src/audit-repository.ts`),
 * so both go through the same server-side whitelist and tiebreaker.
 */
type DashboardAuditSortKey = 'action' | 'outcome' | 'actorRole' | 'occurredAt'

/** Column labels for the live region that announces a new order. */
const AUDIT_COLUMN_LABELS: Readonly<Record<DashboardAuditSortKey, string>> = {
  action: 'Action',
  outcome: 'Outcome',
  actorRole: 'Actor role',
  occurredAt: 'When',
}

/** How many audit rows this preview asks for. Named, because the subtitle quotes it. */
const AUDIT_PREVIEW_LIMIT = 8

/**
 * ★ How often the event wall re-asks, when a reader has switched it on.
 *
 * ⚠ 30 seconds, and the number is a judgement rather than a measurement: fast
 * enough that "live" is not a lie, slow enough that a reader can finish a row.
 * `/api/audit` is a bounded, indexed read (`AUDIT_PREVIEW_LIMIT` rows), so the
 * cost is one small query per open tab per interval — which is why this is
 * acceptable as a client poll and would not be as a server job.
 */
const EVENT_WALL_INTERVAL_MS = 30_000

/**
 * What the audit preview's subtitle may honestly say.
 *
 * ⚠ The static string it replaces — "Last 8 events this scope can see" — became
 * FALSE the moment the table was made sortable. Sorting re-queries the server
 * (`api.audit({ limit: 8, sort, dir })`), so after a click these are the first
 * eight events BY THAT COLUMN, which is not the same set as the eight most
 * recent. A subtitle that keeps saying "last 8" while showing the 8 cheapest,
 * or the 8 alphabetically-first actions, is a sentence the screen contradicts.
 */
function auditPreviewSubtitle(column: DashboardAuditSortKey | null, dir: 'asc' | 'desc', shown: number): string {
  if (column === null) return `The ${shown} most recent events this scope can see`
  const direction = dir === 'asc' ? 'first' : 'last'
  return `The ${direction} ${shown} events by ${AUDIT_COLUMN_LABELS[column]}, not the most recent ${shown}`
}

/**
 * ★★★ WAVE 27 — HOW A DELTA IS ALLOWED TO BE STATED ON THIS PRODUCT.
 *
 * ⚠ **A percentage with no named baseline is a decoration.** Owner-directed on
 * 2026-08-27, and constrained by `ux-direction.md` §5.3 rule 10: every figure that
 * compares periods prints the period it compares against, in the same tile. The
 * baseline here is the SERVER's — the same-length window immediately before the
 * one it charted — and this function never invents one.
 *
 * ⚠ **A previous window of ZERO has no percentage, and says so.** `n/0` is not
 * "infinite growth"; it is "there was nothing to compare with", and printing
 * "▲ ∞%" or silently showing nothing are both worse than the sentence.
 */
function deltaOf(current: number, previous: number): { readonly text: string; readonly direction: 'up' | 'down' | 'flat' } {
  if (previous === 0) {
    return { text: current === 0 ? 'no change: neither period had any' : 'no comparable figure before this period', direction: 'flat' }
  }
  const change = ((current - previous) / previous) * 100
  const rounded = Math.round(change)
  if (rounded === 0) return { text: 'unchanged', direction: 'flat' }
  return {
    text: `${rounded > 0 ? '+' : ''}${rounded.toLocaleString('en-SG')}%`,
    direction: rounded > 0 ? 'up' : 'down',
  }
}

/**
 * The period basis, in the SERVER's words, for every chart and tile on this page.
 *
 * ⚠ **It says "to <date>", never "the last 30 days".** Measured on this estate:
 * 5,005 of 5,150 bills carry a service date in the FUTURE, and a window ending
 * today holds three of them. The window is anchored to the latest activity the
 * caller can see, and the sentence has to say so or it is false — which is the
 * whole of §5.3 rule 10 and the reason `TrendChart` will not render without it.
 */
function periodBasis(series: DashboardSeriesResponse | undefined, formatDate: (iso: string) => string): string {
  if (series === undefined) return 'Loading the period…'
  const { window } = series
  if (window.anchoredTo === 'NO_DATA' || window.from === null || window.to === null) {
    return 'No bill in your scope carries a service date, so there is no period to chart.'
  }
  const basis = `${window.days} days by date of medical service, ${formatDate(window.from)} to ${formatDate(window.to)}`
  /*
   * ⚠ A WINDOW DRAWN AROUND FUTURE-DATED WORK SAYS SO. The anchor prefers the
   * latest service date up to today; it falls back to future-dated work only when
   * the scope has nothing on or before today, and a caption that called that "the
   * latest activity" would be describing treatment that has not happened.
   */
  if (window.anchoredTo === 'FUTURE_DATED_ONLY') {
    return `${basis}: every bill in your scope is dated in the FUTURE, so this window covers work that has not happened yet.`
  }
  return `${basis}: the latest activity your scope can see, not the calendar's last ${window.days} days.`
}

/**
 * A measure tile with its trend beside it.
 *
 * ⚠ The sparkline is `aria-hidden` and decorative BY CONSTRUCTION — the delta is
 * printed as words in the same tile, so the trend is never shape-only. See
 * `Sparkline.tsx`'s own header for the four rules it enforces.
 */
function TrendCard({
  chipClass,
  icon,
  label,
  value,
  delta,
  baseline,
  points,
}: {
  chipClass: string
  icon: ReactNode
  label: string
  value: number
  delta: { readonly text: string; readonly direction: 'up' | 'down' | 'flat' }
  baseline: string
  points: readonly { readonly date: string; readonly count: number }[]
}) {
  const arrow = delta.direction === 'up' ? '\u25b2' : delta.direction === 'down' ? '\u25bc' : ''
  return (
    <div className="pt-card">
      <span className={`pt-chip ${chipClass}`} aria-hidden="true">
        {icon}
      </span>
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--sgds-color-subtle)] mt-4">{label}</p>
      <p className="text-[33px] leading-[40px] font-semibold pt-tabular mt-1">{value.toLocaleString('en-SG')}</p>
      {/*
        ⚠ ARROW AND COLOUR DECORATE A SENTENCE THAT ALREADY SAYS IT — `ux-direction.md`
        §4's own words. The direction is in the text, so a reader who sees neither the
        glyph nor the hue loses nothing.
      */}
      <p className="text-sm text-[var(--sgds-color-subtle)] mt-1">
        {arrow.length > 0 ? `${arrow} ` : ''}
        {delta.text} {baseline}
      </p>
      <div className="mt-2">
        <Sparkline points={points} label={`${label} across the charted period`} />
      </div>
    </div>
  )
}

function MeasureCard({
  chipClass,
  icon,
  label,
  value,
  footnote,
}: {
  chipClass: string
  icon: ReactNode
  label: string
  value: number
  footnote: string
}) {
  return (
    <div className="pt-card">
      <span className={`pt-chip ${chipClass}`} aria-hidden="true">
        {icon}
      </span>
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--sgds-color-subtle)] mt-4">{label}</p>
      <p className="text-[33px] leading-[40px] font-semibold pt-tabular mt-1">{value.toLocaleString('en-SG')}</p>
      <p className="text-sm text-[var(--sgds-color-subtle)] mt-1">{footnote}</p>
    </div>
  )
}

/**
 * ★★★ WAVE 27 — THE INTERFACE AND BATCH ALERTS TILE.
 *
 * This card said *"Not built yet"* until 2026-08-27, and it was the only one of
 * the dashboard's four unbuilt cards that was unbuilt merely because nobody had
 * got to it. The other three are blocked on something real — a measure whose
 * numerator is structurally empty, a transport that does not exist in this
 * deployment, an artefact class with one instance — and `docs/HANDOFF.md` §W27
 * ranks all four. The feed behind this one was already there: a delivery refused
 * whole at the interface, records refused inside accepted deliveries, each with a
 * machine-readable reason, each already audited and already scope-filtered by the
 * repository that serves `/records`.
 *
 * ⚠ **EVERY NUMBER HERE IS THE SERVER'S (S-011).** The shares are two server
 * integers rendered as a sentence; nothing is summed, divided or classified in
 * the browser beyond the headline, whose two addends are printed underneath it.
 *
 * ⚠ **THE CITATION WAS WRONG AND IS FIXED HERE.** The unbuilt card cited *"P2C10
 * Table 10-1"* as defining the failures it would surface. **Table 10-1 is
 * "Definition of Severity Level"** — a three-row High/Medium/Low taxonomy over
 * Business Operations, Data Leak and Security, governing how severely a problem
 * is rated once reported (P2C10 §1.1.4). It defines no interface or batch failure
 * type at all; the neighbouring Table 10-2 is the response/resolution Service
 * Level. An evaluator who looked it up found a severity table under a card
 * implying it was an alert catalogue — the citation defect class the RTM records
 * six times, and the second instance of it to reach a screen. What Table 10-1
 * legitimately governs is the SEVERITY a surfaced failure is assigned, and that
 * is what the card now says.
 *
 * ⚠ **NO SEVERITY IS ASSIGNED ON SCREEN.** Table 10-1's levels turn on business
 * impact, data leakage and security exposure — judgements about an incident, not
 * properties of a refusal reason. Printing "High" beside `RECORD_MALFORMED`
 * because this component decided so would be exactly the fabrication the unbuilt
 * cards existed to avoid. The card names the scale the Authority rates against
 * and stops there.
 */
function InterfaceAlertsCard({ alerts }: { alerts: ApiFetchState<InterfaceAlertCountsResponse> }) {
  if (alerts.error !== null) {
    return <ErrorCard title="Could not load interface alerts" message={alerts.error} onRetry={alerts.reload} />
  }

  const data = alerts.data
  if (data === undefined) return <LoadingCard label="Loading interface alerts" rows={3} />

  const quiet = data.filesRejected === 0 && data.recordsRejected === 0
  return (
    <div className="pt-card">
      <span className="pt-chip pt-chip-amber" aria-hidden="true">
        {ICONS.alert}
      </span>
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--sgds-color-subtle)] mt-4">
        Interface &amp; batch alerts
      </p>

      {quiet ? (
        <>
          <p className="text-[33px] leading-[40px] font-semibold pt-tabular mt-1">0</p>
          <p className="text-sm text-[var(--sgds-color-subtle)] mt-1">
            No delivery and no record in your scope has been refused, over{' '}
            {data.filesSeen.toLocaleString('en-SG')} deliveries and {data.recordsSeen.toLocaleString('en-SG')} records.
          </p>
        </>
      ) : (
        <>
          <p className="text-[33px] leading-[40px] font-semibold pt-tabular mt-1">
            {(data.filesRejected + data.recordsRejected).toLocaleString('en-SG')}
          </p>
          {/*
            ⚠ The headline adds two SERVER integers and both are named immediately
            below it, because they are not the same kind of failure: a refused
            DELIVERY means a counterparty's whole file never entered the system, and
            a refused RECORD means the file was taken and one line in it was not. One
            number with no split would read as a single incident class and hide the
            more serious of the two.
          */}
          <p className="text-sm text-[var(--sgds-color-subtle)] mt-1">
            {data.filesRejected.toLocaleString('en-SG')} of {data.filesSeen.toLocaleString('en-SG')} deliveries refused
            whole; {data.recordsRejected.toLocaleString('en-SG')} of {data.recordsSeen.toLocaleString('en-SG')} records
            refused inside an accepted delivery.
          </p>

          {data.fileReasons.length > 0 && (
            <>
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--sgds-color-subtle)] mt-4">
                Deliveries refused
              </p>
              <ul className="mt-1 flex flex-col gap-1">
                {data.fileReasons.map((reason) => (
                  <li key={`file-${reason.reasonKind}`} className="text-sm flex justify-between gap-3">
                    <span>{reason.reasonKind}</span>
                    <span className="pt-tabular font-semibold">{reason.count.toLocaleString('en-SG')}</span>
                  </li>
                ))}
              </ul>
              {/*
                ⚠ A refused FILE deliberately carries NO link. `/records` lists
                `app.inbound_record` rows and a file refused at the interface produced
                NONE of them — it was turned away before it was parsed. A link here
                would land on an empty enquiry and read as a bug in the count beside
                it, which is the "ship the way out" rule applied in reverse: do not
                offer a door that opens onto nothing.
              */}
              <p className="text-xs text-[var(--sgds-color-subtle)] mt-1">
                A refused delivery has no records to list: it was turned away before it was read.
              </p>
            </>
          )}

          {data.recordReasons.length > 0 && (
            <>
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--sgds-color-subtle)] mt-4">
                Records refused
              </p>
              <ul className="mt-1 flex flex-col gap-1">
                {data.recordReasons.map((reason) => (
                  <li key={`record-${reason.reasonKind}`} className="text-sm flex justify-between gap-3">
                    <Link to={`/records?outcome=REJECTED&reasonKind=${encodeURIComponent(reason.reasonKind)}`}>
                      {reason.reasonKind}
                    </Link>
                    <span className="pt-tabular font-semibold">{reason.count.toLocaleString('en-SG')}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}

      <p className="text-xs text-[var(--sgds-color-subtle)] mt-4 pt-3 border-t border-[var(--sgds-border-color-muted)]">
        Counted over the deliveries you can read. Each failure is rated High, Medium or Low against the severity
        levels PSD defined; this card assigns no severity of its own.
      </p>
    </div>
  )
}

const ICONS = {
  /**
   * The alerts tile — a warning triangle rather than the `cross` two entries down.
   * A refused record is a counterparty's file failing a rule, not this system
   * erroring, and the two must not share a glyph.
   */
  alert: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
      <path d="M12 4.5 21 19.5H3L12 4.5Z" strokeLinejoin="round" />
      <path d="M12 10v4" strokeLinecap="round" />
      <circle cx="12" cy="16.6" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  ),
  stack: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
      <path d="M4 17 9 10l4 4L20 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  clock: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3.2 2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  check: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
      <path d="M4 12.5 9.5 18 20 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  cross: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
      <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
    </svg>
  ),
  /**
   * The level 2 stage: a clock that has already been moved on once.
   *
   * ⚠ The level 1 and level 2 tiles share the AMBER chip and differ by glyph,
   * and that is a measured decision rather than a shortcut. The obvious fourth
   * hue would be `pt-chip-accent`, whose foreground resolves through
   * `--sgds-accent-color-emphasis` -> `--sgds-blue-700` -> `#0151A0` in the
   * installed package's `themes/root.css`. Against PSD navy `#00405F` — the
   * "Bills in scope" chip sitting two tiles away in the same row — that is
   * **1.41:1, ΔE 11.5**: exactly the pair `ux-direction.md` §2.5 names as below
   * the discriminability floor and forbids putting on screen together. Sharing
   * the amber hue also says something true, which a fourth colour would not:
   * both tiles are outstanding work, at two stages of the same queue.
   */
  stage2: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
      <circle cx="10" cy="12" r="7.5" />
      <path d="M10 7.5V12l2.6 1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M17.5 8.5 21 12l-3.5 3.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
}

/**
 * ★ TASK 5D. `GET /api/bills` is now paginated (P2C3 §7.1.6 — see
 * `services/gateway/src/routes/bills.ts`'s own header), so this preview asks
 * for the largest single SAFE page rather than nothing (which now defaults to
 * a smaller page than this screen used to silently receive). Named the same
 * way `AUDIT_PREVIEW_LIMIT` is, because it is the same kind of number: the
 * most this page will ever show without walking every page of the caller's
 * scope, which nothing here does. `MAX_BILL_LIMIT` in `@prs2/billing` carries
 * the byte-budget arithmetic this literal must not drift from.
 */
const BILLS_SNAPSHOT_LIMIT = 60

export function DashboardPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const bills = useApi(() => api.bills({ limit: BILLS_SNAPSHOT_LIMIT }), [])
  // ★★ WAVE 8 (Task 2, S-029). A SECOND request, and the four breakdown tiles read
  // it instead of counting `bills.data.bills`. See `counts` below for what that
  // changed and what it deliberately did not.
  const statusCounts = useApi(() => api.billStatusCounts(), [])
  // ★★★ WAVE 27 — the interface and batch failure feed, counted in SQL under the
  // same scope predicate `/api/records` applies. See `InterfaceAlertsCard`.
  const alerts = useApi(() => api.recordAlertCounts(), [])
  // ★★★ WAVE 27 — the trend. One request; every chart and every delta on this
  // page reads it, so no two figures here can disagree about their period.
  /**
   * ★★★ WAVE 27 (owner-directed) — THE PERIOD IS THE READER'S TO CHOOSE.
   *
   * ⚠ **The route CLAMPS this and echoes the value it used**, and every caption on
   * this page is built from the response rather than from this state. So a reader
   * who asks for 9,999 days gets 90 and the page says 90 — a screen can never print
   * a period it did not get, which is the property `?days=` was built with.
   *
   * ⚠ The options stop at 90 because the route's own ceiling is 90: a window is a
   * `generate_series` row count, not a comfort setting, and offering a control that
   * the server will silently narrow is how a caption comes to disagree with its own
   * chart.
   */
  const [days, setDays] = useState(30)
  const series = useApi(() => api.billSeries(days), [days])
  const { sort: auditSort, onSort: onAuditSort, interacted: auditSorted } = useSort<DashboardAuditSortKey>()
  const audit = useApi(
    () => api.audit({ limit: AUDIT_PREVIEW_LIMIT, ...sortParams(auditSort) }),
    [auditSort.column, auditSort.dir],
  )

  const rows: readonly Bill[] = bills.data?.bills ?? []
  const auditEvents = audit.data?.events ?? []

  /**
   * ★★★ WAVE 15 — P2C1 §12.83(f), the "self-refresh event wall".
   *
   * The clause asks for *"a mini self-refresh wall to show streams of events
   * relevant to the function or page"*. The card below already IS the event
   * stream; what it was missing is the self-refresh.
   *
   * ⚠ **OFF when the page loads, and that is a deliberate reading of the clause
   * rather than a hedge.** Two reasons, both of them this product's own rules:
   *
   *  1. **An audit trail that reloads under a reader is hostile.** An officer
   *     reading row four when eight new rows arrive has lost their place in a
   *     compliance record. A poll the reader switched on is a poll they expect.
   *  2. **Nothing in this deployment runs on its own** (ADR 0009 Decision 4), and
   *     four other surfaces say so in those words. A wall that refreshed by
   *     default would be the first thing on any screen that appeared to, and a
   *     viewer would reasonably generalise from it.
   *
   * ⚠ **This is a CLIENT poll and is not a scheduler.** It re-asks the same
   * `GET /api/audit` this card already calls, from the browser, while the tab is
   * open. Nothing server-side is started, and closing the tab ends it — the card
   * says so beneath the control.
   */
  const [wallLive, setWallLive] = useState(false)
  useEffect(() => {
    if (!wallLive) return undefined
    const timer = setInterval(() => {
      audit.reload()
    }, EVENT_WALL_INTERVAL_MS)
    // ⚠ Cleared on unmount AND on the flag going false — without the second, a
    // reader who switched it off would leave a timer re-querying the audit trail
    // for as long as the tab lived.
    return () => {
      clearInterval(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallLive])
  // ★★ WAVE 8 (Task 2, S-029) — THE FIVE MEASURE TILES NOW COUNT THE WHOLE SCOPE.
  //
  // ⚠ WHAT WAS WRONG. `total` was the gateway's scope-filtered count and correct,
  // while the four tiles beneath it were `rows.filter(...).length` over `rows` —
  // one page, capped at `BILLS_SNAPSHOT_LIMIT`, in the server's DEFAULT order (by
  // service date, not by status). So the moment a scope held more bills than a
  // page, the four figures were arithmetically wrong and did not sum to the total
  // printed above them. The seeded demonstration corpus makes that the ordinary
  // case, not the edge: 5,040 bills for a PSD identity, 1,020 of them awaiting a
  // decision, against a 60-row page. It was disclosed in a footnote and still
  // wrong, and this file's own comment named the fix.
  //
  // ⚠ ALL FIVE FIGURES COME FROM ONE STATEMENT, and that is why `total` moved off
  // `bills.data.total` too. `GET /api/bills/status-counts` answers `total` as the
  // SUM of its own groups, so the parts provably sum to the whole; taking the whole
  // from the list request instead would put two counts from two round trips beside
  // each other, and a breakdown that does not sum to its own total is the exact
  // defect this task exists to remove.
  //
  // ⚠ THE CLASSIFICATION STAYS ON THIS SIDE, AND IT NOW LIVES IN ONE MODULE.
  // The server reports one count per `app.bill.status` and decides nothing;
  // `pendingDecision()` — the same definition the approval queue's membership and
  // the lifecycle button gate read — is what maps SUBMITTED/AWAITING_L1 to level 1
  // and AWAITING_L2 to level 2. That is one sentence with one home, not a server
  // copy and a client copy kept in step by nobody (S-011). Its own local
  // `AWAITING_L1_STATUSES` literal is how the level 2 stage came to be countable
  // here and never counted (I9).
  //
  // ⚠ ★★ REVIEW FIX (Important 1) — THE INVARIANT THAT MAKES THESE TILES ADD UP IS
  // NOW GUARDED, AND IT WAS NOT. `total` is the SUM of every group the endpoint
  // returned, while the four tiles below it cover SUBMITTED/AWAITING_L1,
  // AWAITING_L2, APPROVED and REJECTED — and the inline classifier that used to sit
  // here SKIPPED any other group in silence. A sixth status added by a migration
  // would have landed in `total` and in no tile, so the four would have stopped
  // summing to the figure above them: the arithmetic mismatch this task exists to
  // remove, re-entering through the door the fix itself left open. Two things close
  // it, because they cover two different states:
  //   · `lib/status-breakdown.ts` owns the four predicates, and
  //     `status-breakdown.test.ts` asserts that EXACTLY ONE of them claims every
  //     member of `BILL_STATUSES` — itself asserted equal to `app.bill`'s own
  //     `status` CHECK constraint. That fails in CI, before a build ships.
  //   · `breakdownOf` reports what it could NOT place (`unclassified`), and the
  //     sixth tile below renders it. That fires on SCREEN, in the one state no
  //     checked-in guard can see: a database ahead of the bundle reading it.
  const counts = breakdownOf(statusCounts.data ?? { total: 0, byStatus: {} })
  const total = counts.total
  // ⚠ ★★ REVIEW FIX (Important 1, second pass) — the flag the decision card's
  // subtitle, empty state and "Open the approval queue" gate all switch on. When
  // true, `counts` above is the `{ total: 0, byStatus: {} }` FALLBACK, not a real
  // answer — so `awaitingAnyone` below reads `0`, and printing it unlabelled would
  // be exactly the "five zeroes over a populated scope" fabrication this page must
  // never produce. See the block comment above the render gate for what this does
  // and does not suppress.
  const countsFailed = statusCounts.error !== null
  // ⚠ STILL PAGE-DERIVED, AND STILL DISCLOSED. The composition chart counts
  // `rows`, and the "Awaiting your decision" list is `rows` filtered by
  // `canActOn` — both bounded by one page. They are partial for DIFFERENT reasons,
  // and only one of them is unanswerable: `GROUP BY b.bill_type` is exactly as
  // countable as `GROUP BY b.status`, so the chart is partial because this task
  // built ONE read and not two — unbuilt, not unbuildable. The list genuinely is
  // not a `GROUP BY` at all: "may THIS identity act on it" is a per-bill
  // authorisation question over the bill's agency, type, level and monetary limit,
  // and answering it for a whole scope in SQL would mean a SECOND implementation of
  // `authorise()` beside the one the gateway asks — the S-011 shape (one rule, two
  // copies, kept in step by nobody) this repository has logged repeatedly. So the
  // footnote the four tiles no longer need is exactly the footnote these two still
  // carry. Dropping it with them would have been worse than the arithmetic bug: a
  // partial figure that looks whole.
  //
  // ⚠ ★★ REVIEW FIX (Minor 5, first pass) — THIS COMPARES ONE RESPONSE WITH ITSELF.
  // `pageBounded` used to read the COUNTS total against the LIST's rows: two
  // separate round trips, so a bill created between them made `total === rows.length`
  // hold while the page was genuinely one row short, and the composition subtitle
  // then printed the COMPLETENESS form over an incomplete page — the same fail-open
  // shape as the in-flight defect this task already fixed one screen over, with a
  // millisecond window and a one-row consequence instead. `bills.data.total` is the
  // list response's own count of the population it paged, which is the right
  // comparator for "did this page cover the list" and is what `ApprovalQueuePage`
  // already uses. The composition subtitle prints `listTotal` for the same reason:
  // that sentence is a claim about the LIST request, so both of its numbers come
  // from the list request.
  //
  // ⚠ ★★ REVIEW FIX (Minor 1, second pass) — AND THE MIRROR SKEW GATED THE WRONG
  // THING. `listTotal > rows.length` is the right test for the composition
  // subtitle's OWN sentence, but the same flag also gates the decision card's
  // disclosure, whose number (`awaitingAnyone`, below) is COUNTS-derived. Both
  // requests fire on mount, so the mirror of the already-tested skew is equally
  // possible: if `/api/bills/status-counts` sees a bill `/api/bills` does not yet
  // (rather than the other way round), `listTotal === rows.length` while the tiles
  // report one MORE bill in scope than the page holds — the chart then prints the
  // completeness form and the decision subtitle drops "across your scope", over a
  // page that is short of the scope. Same fail-open shape the queue closed on, the
  // other population skewed. Disclosing on whichever total is larger closes it
  // without touching the composition sentence's own number, which stays `listTotal`
  // for the reason above.
  const listTotal = bills.data?.total ?? 0
  const pageBounded = Math.max(listTotal, counts.total) > rows.length
  const byType = { NEW: 0, UPWARD_ADJUSTMENT: 0, DOWNWARD_ADJUSTMENT: 0 } as Record<string, number>
  for (const bill of rows) byType[bill.billType] = (byType[bill.billType] ?? 0) + 1
  const maxByType = Math.max(1, ...Object.values(byType))

  // ⚠ "Awaiting YOUR decision" now means what it says (I7). It used to list
  // every SUBMITTED/AWAITING_L1 bill in scope regardless of whether this
  // identity held any approval authority over it, so a level 0 HSP_USER was
  // shown a work queue of things it could not do and no bill awaiting level 2
  // at all. `canActOn` asks `authorise()` — the gateway's own function — the
  // same question the server will ask, across BOTH levels.
  const scope = user?.scope ?? null
  const actionable = scope === null ? [] : rows.filter((b) => canActOn(scope, b))
  const awaitingRows = actionable.slice(0, 5)
  const awaitingAnyone = counts.awaitingL1 + counts.awaitingL2

  const isRecovery = user?.scope.role === 'RECOVERY_USER'

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl mb-2">Claims processing overview</h1>
        <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
          Figures below are computed from the bills you can see as {ROLE_LABELS[user?.scope.role ?? 'PSD_USER']}:
          signing in as a different officer changes every count on this page. A tile that PSD has not yet defined a
          measure for says so, rather than showing an invented figure.
        </p>
      </div>

      {/*
        ★ F-20: the MSO rule engine is one click from here. It used to be reachable only through a menu group that is
        closed on a fresh profile, which cost a presenter five clicks to the first result. Shown to the officers the
        MSO routes answer to (PSD), by the same predicate the routes call, so it is never a link that refuses.
      */}
      {scope !== null && mayAdministerReferenceData(scope) && (
        <section aria-label="MSO rule engine" className="pt-card flex flex-wrap items-center justify-between gap-4" data-testid="dashboard-mso">
          <div className="max-w-[60ch]">
            <h2 className="text-lg font-semibold mb-1">MSO rule engine</h2>
            <p className="text-sm text-[var(--sgds-body-color-subtle)] m-0">
              Price a bill under the MSO rules, read what the engine recorded, or compare it with the flat-rate path.
            </p>
          </div>
          <ul className="flex flex-wrap gap-3 list-none p-0 m-0">
            <li>
              <Link to="/mso/calculator" className="pt-linkbtn">
                MSO calculator
              </Link>
            </li>
            <li>
              <Link to="/mso/calculations" className="pt-linkbtn">
                MSO calculation enquiry
              </Link>
            </li>
            <li>
              <Link to="/mso/comparison" className="pt-linkbtn">
                MSO engine comparison
              </Link>
            </li>
          </ul>
        </section>
      )}

      {/*
        ⚠ ★★ WAVE 8 (Task 2). A failed `/api/bills` request gates the WHOLE block:
        the composition chart and the decision list are both built from `rows`, so
        without a list response neither has anything to render. `onRetry` reloads it.

        ⚠ ★★ REVIEW FIX (Minor 8 → revisited, Important 1 of the second review
        pass) — A FAILED COUNTS REQUEST NO LONGER BLANKS THIS WHOLE BLOCK.
        `/api/bills/status-counts` gates only the surfaces that are actually built
        from it: the five measure tiles, the "Other status" tile, and the
        reconciliation notice. The composition chart is UNAFFECTED, and that is not
        a new exemption — it never quoted a counts figure. Its subtitle prints
        `listTotal` (`bills.data.total`, Minor 5 of the first review pass) and
        `rows.length`, both from the LIST response, precisely so it cannot claim
        "the first N of M bills in scope" using two different round trips' numbers.
        The decision card's list is the same: `actionable` is `rows.filter(canActOn)`,
        list-derived throughout. Only its SUBTITLE, its empty state, and the
        "Open the approval queue" button's gate quote `awaitingAnyone` — a
        counts-derived figure — and those three are what `countsFailed` below
        switches to a sentence that does not claim a number nobody has.

        The comment this replaces argued the opposite from a premise that was true
        when written and stopped being true in the SAME commit: it said "every
        sentence in both cards quotes a counts figure", but the composition card's
        only numeric sentence had already been moved onto `listTotal` by the fix
        beside it. Checked again after that fix: the composition card costs ZERO
        new sentence variants for this state, because it has nothing counts-derived
        to suppress. Only the decision card's three counts-derived spots needed a
        countsFailed branch, not "four new sentence variants" — and a demonstration
        where one failed request blanks the whole block is worse than one where the
        chart, and the page-derived half of the decision list, still render.
      */}
      {bills.error !== null ? (
        <ErrorCard title="Could not load bills" message={bills.error} onRetry={bills.reload} />
      ) : bills.initialLoading || statusCounts.initialLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
          {[0, 1, 2, 3, 4].map((i) => (
            <LoadingCard key={i} label="Loading measure" rows={1} />
          ))}
        </div>
      ) : (
        <>
          {statusCounts.error !== null ? (
            <ErrorCard title="Could not load the bill breakdown" message={statusCounts.error} onRetry={statusCounts.reload} />
          ) : (
            <>
              {/*
                Five tiles, not four. "Awaiting level 2" is the I9 half that was
                computed and never rendered — a bill that had cleared level 1 was in
                no count on this page and in no queue, so the only people who could
                action it had no way to find it. The two levels are separate tiles
                rather than one combined "awaiting a decision" because they are
                waiting on DIFFERENT people, and a single number hides that.
              */}
              <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5" aria-label="Measures">
                <MeasureCard
                  chipClass="pt-chip-navy"
                  icon={ICONS.stack}
                  label="Bills in scope"
                  value={counts.total}
                  footnote="Every bill your current scope can read"
                />
                {/*
                  ★★ WAVE 8 (Task 2). The four footnotes that read "of the first N
                  loaded (M in scope)" are GONE, because the figures they qualified are
                  no longer of the first N loaded — each is the count over the whole
                  scope, from `/api/bills/status-counts`. A disclosure kept over a
                  figure that is now whole would be a false statement in the other
                  direction, and it would train a reader to ignore the ones that are
                  still true one card down.
                */}
                <MeasureCard
                  chipClass="pt-chip-amber"
                  icon={ICONS.clock}
                  label="Awaiting level 1"
                  value={counts.awaitingL1}
                  footnote="Submitted or awaiting a level 1 decision, not all yours to make"
                />
                <MeasureCard
                  chipClass="pt-chip-amber"
                  icon={ICONS.stage2}
                  label="Awaiting level 2"
                  value={counts.awaitingL2}
                  footnote="Cleared level 1, waiting on a level 2 approver"
                />
                <MeasureCard
                  chipClass="pt-chip-green"
                  icon={ICONS.check}
                  label="Approved"
                  value={counts.approved}
                  footnote="Reached APPROVED in this scope"
                />
                <MeasureCard
                  chipClass="pt-chip-red"
                  icon={ICONS.cross}
                  label="Rejected"
                  value={counts.rejected}
                  footnote="Reached REJECTED in this scope"
                />
                {/*
                  ⚠ ★★ REVIEW FIX (Important 1) — THE SIXTH TILE, WHICH SHOULD NEVER
                  APPEAR, AND IS THE POINT.

                  `Bills in scope` is the SUM of every group the endpoint returned. The
                  four tiles above cover the five statuses `app.bill`'s CHECK constraint
                  admits today — and `status-breakdown.test.ts` fails in CI if a sixth is
                  added to that constraint, to `BILL_STATUSES`, or to a tile that another
                  tile already claims. What that guard CANNOT see is a DATABASE ahead of
                  the bundle reading it: a migration applied to a deployed environment
                  whose officer-web build predates it. In that state every test is green,
                  the endpoint counts the new status into `total`, and the four tiles
                  would under-count in silence — the exact arithmetic mismatch this task
                  exists to remove.

                  So the residual is RENDERED rather than absorbed, and it names the
                  statuses it could not place. The four-plus-this then always add up to
                  the figure above them, whatever the database holds. Its absence is the
                  normal state; its presence is a deployment fact an officer can report.
                */}
                {counts.unclassified > 0 && (
                  <MeasureCard
                    chipClass="pt-chip-navy"
                    icon={ICONS.stack}
                    label="Other status"
                    value={counts.unclassified}
                    footnote={`Counted in the total above and in no tile: ${counts.unclassifiedStatuses.join(', ')}. This build does not recognise ${counts.unclassifiedStatuses.length === 1 ? 'that status' : 'those statuses'}; the database is ahead of it.`}
                  />
                )}
              </section>

              {/*
                ⚠ ★★ REVIEW FIX (Important 1), the other half of the invariant. The five
                figures above are the endpoint's groups, classified; `total` is the
                endpoint's own `total`. `bill-status-counts.test.ts` asserts against SQL
                that the second IS the sum of the first, and this sentence refuses to
                trust that it cannot stop being true. A breakdown whose parts do not add
                up to its own whole is what Task 2 removed — saying so beats printing six
                numbers that quietly do not reconcile.
              */}
              {!counts.reconciled && (
                <div className="pt-well p-4" role="status">
                  <p className="text-sm">
                    The figures above do not add up: the breakdown&rsquo;s parts total{' '}
                    <span className="pt-tabular font-semibold">
                      {(counts.awaitingL1 + counts.awaitingL2 + counts.approved + counts.rejected + counts.unclassified).toLocaleString('en-SG')}
                    </span>{' '}
                    against a reported <span className="pt-tabular font-semibold">{total.toLocaleString('en-SG')}</span> bills in
                    scope. Treat every figure in this row as unverified and report it: one grouped statement answered both, so
                    they cannot legitimately differ.
                  </p>
                </div>
              )}
            </>
          )}

          {/*
            ★★★ WAVE 15 — P2C1 §12.83(k) "Charting" and §12.84(b) "Charts that
            offer drill down on detailed information".
            ⚠ **This chart is drawn from `statusCounts`, and the card below it from
            `rows`, and the difference is the whole reason they are two cards
            rather than one.** `byStatus` is a `GROUP BY` over the caller's entire
            scope; the composition meters count one loaded page and say so. Putting
            a whole-scope chart and a page-bounded one in the same card would make
            the disclosure on one read as decoration on both.
            ⚠ It renders only when the counts actually loaded — `statusCounts.error`
            already routes to an ErrorCard above, and `counts` falls back to
            `{ total: 0, byStatus: {} }`, so a chart drawn from the fallback would
            be five zero-length bars over a populated estate. That is the
            fabricated-measurement shape this page must never produce.
          */}
          <section className="pt-card" aria-labelledby="status-chart-heading">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h2 className="text-lg" id="status-chart-heading">
                  Where the estate stands
                </h2>
                <p className="text-sm text-[var(--sgds-color-subtle)] mt-1">
                  Every bill your scope can read, by lifecycle stage: counted by the server over the whole scope, not
                  from a loaded page. Open a stage to see its bills.
                </p>
              </div>
            </div>
            <StatusBarChart
              // ⚠ LIFECYCLE order, from the shared `BILL_STATUSES` — the same list
              // `status-breakdown.test.ts` asserts equal to `app.bill`'s own CHECK
              // constraint. A hand-written order here would be a second copy that
              // drifts the day a status is added, and the chart would silently drop
              // it.
              // ⚠ From the RAW payload, not from `counts`: `breakdownOf` folds the
              // statuses into five TILES (two of which merge several statuses), so it
              // has no per-status map to read. A status the bundle does not know is
              // therefore absent from this chart and present in the `unclassified`
              // tile above — which is the honest split: the tile can report an
              // unknown status, and a bar has no label to draw for one.
              bars={BILL_STATUSES.map((status) => ({
                status,
                count: statusCounts.data?.byStatus[status] ?? 0,
              }))}
              total={total}
              drillDownTo={(status) => `/bills?status=${encodeURIComponent(status)}`}
            />
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="pt-card">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-lg">Bill composition by adjustment indicator</h2>
                  {/*
                    ⚠ ★★ WAVE 8 (Task 2) — THIS DISCLOSURE STAYS, and `DashboardPage.dom.test.tsx`
                    asserts that it does. The five tiles above became whole-scope
                    counts; this chart did not, because `billType` has no
                    count-by-type read and this task built one. So it is still the
                    first page of the caller's scope, and it still says so — now
                    beneath four figures that are exact, which makes the contrast
                    the whole point rather than a detail.
                  */}
                  <p className="text-sm text-[var(--sgds-color-subtle)] mt-1">
                    {pageBounded
                      ? `Counted from the first ${rows.length} of ${listTotal} bills in scope · NB / UA / DA`
                      : 'Counted from bills in scope · NB / UA / DA'}
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-4">
                {Object.entries(byType).map(([type, count]) => (
                  <div key={type}>
                    <div className="flex justify-between text-sm mb-1">
                      <span>
                        <BillTypeBadge billType={type} /> <span className="text-[var(--sgds-color-subtle)]">({BILL_TYPE_SHORT[type]})</span>
                      </span>
                      <span className="pt-tabular font-semibold">{count}</span>
                    </div>
                    <div className="pt-bar-track">
                      <div
                        className="pt-bar-fill"
                        style={{ width: `${(count / maxByType) * 100}%`, background: 'var(--pt-series-1)' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-card">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-lg">Awaiting your decision</h2>
                  {/*
                    The subtitle states what the list actually contains: bills
                    THIS identity may approve or reject, at either level, capped
                    at five. It used to say "Submitted or awaiting a level 1
                    decision" over a list that ignored authority entirely (I7)
                    and excluded level 2 altogether (I9).

                    ⚠ ★★ WAVE 8 (Task 2) — THIS SENTENCE NOW MIXES TWO KINDS OF
                    FIGURE, and says which is which. `awaitingAnyone` became a
                    WHOLE-SCOPE count (it is the two tiles above, summed), while
                    `actionable.length` is still the bills this identity may act on
                    WITHIN THE LOADED PAGE — `canActOn` asks a per-bill
                    authorisation question about agency, type, level and monetary
                    limit, which is not something a `GROUP BY` can answer. Left
                    unlabelled, "12 of 40" would read as "you may act on 12 of the
                    40 in your scope" when the true claim is "12 of the 60 loaded".
                    That is a new false sentence created by fixing the tiles, so it
                    is marked here rather than discovered later.

                    ⚠ ★★ REVIEW FIX (Important 1, second review pass) — AND NOW A
                    FOURTH STATE: the counts request itself failed. `awaitingAnyone`
                    is counts-derived, so with `statusCounts.error !== null` it would
                    read the `{ total: 0, byStatus: {} }` FALLBACK's `0` — printing
                    "0 awaiting a decision" over a scope this page never asked about,
                    the exact fabricated-measurement shape this page must never
                    produce. `countsFailed` switches every use of `awaitingAnyone` in
                    this card to a sentence that says the whole-scope figure could
                    not be loaded, rather than a number that looks like an answer.
                    `actionable`/`awaitingRows` are unaffected — they are `rows`
                    filtered by `canActOn`, list-derived throughout — so the page's
                    own actionable bills still render.
                  */}
                  <p className="text-sm text-[var(--sgds-color-subtle)] mt-1">
                    {countsFailed
                      ? actionable.length === 0
                        ? `Bills your grants let you approve or reject: none in the first ${rows.length} loaded. The total awaiting a decision across your scope could not be loaded.`
                        : `The first ${awaitingRows.length} of ${actionable.length} your grants let you approve or reject in the first ${rows.length} loaded. The total awaiting a decision across your scope could not be loaded.`
                      : actionable.length === 0
                        ? pageBounded
                          ? `Bills your grants let you approve or reject: none in the first ${rows.length} loaded, of ${awaitingAnyone} awaiting a decision across your scope`
                          : `Bills your grants let you approve or reject: none of the ${awaitingAnyone} awaiting a decision`
                        : pageBounded
                          ? `The first ${awaitingRows.length} of ${actionable.length} your grants let you approve or reject in the first ${rows.length} loaded, out of ${awaitingAnyone} awaiting a decision across your scope`
                          : `The first ${awaitingRows.length} of ${actionable.length} your grants let you approve or reject, out of ${awaitingAnyone} awaiting a decision`}
                  </p>
                </div>
              </div>
              {awaitingRows.length === 0 ? (
                <EmptyCard
                  // "Nothing is waiting" and "things are waiting, but not on
                  // you" are different facts and used to render identically.
                  //
                  // ⚠ ★★ WAVE 8 (Task 2) — AND THERE IS NOW A THIRD FACT. With
                  // `awaitingAnyone` counting the whole scope and `actionable` only
                  // the loaded page, "none of them are yours" is a claim this
                  // screen can no longer make while a page is all it has read: the
                  // ones this officer may decide could simply be further down the
                  // scope. The message says what is actually known, and the button
                  // below it is the way to the rest — a refusal ships with its way
                  // out, which is the lesson the button's own placement records.
                  //
                  // ⚠ ★★ REVIEW FIX (Important 1, second pass) — A FOURTH FACT:
                  // "nothing is waiting" cannot be claimed at all when the counts
                  // request failed, so `countsFailed` takes priority over
                  // `awaitingAnyone === 0` here — an unproven all-clear is not one.
                  title={
                    countsFailed
                      ? 'Could not confirm anything awaiting a decision'
                      : awaitingAnyone === 0
                        ? 'Nothing awaiting a decision'
                        : 'Nothing awaiting YOUR decision'
                  }
                  message={
                    countsFailed
                      ? `None of the first ${rows.length} loaded here are yours to approve or reject, and the total awaiting a decision across your scope could not be loaded. Retry the breakdown above, or open the approval queue to check directly.`
                      : awaitingAnyone === 0
                        ? 'Every bill in scope has been approved or rejected.'
                        : pageBounded
                          ? `${awaitingAnyone} ${awaitingAnyone === 1 ? 'bill is' : 'bills are'} still awaiting a decision across your scope, and none of the first ${rows.length} loaded here are yours to approve or reject. There may be others further down your scope: open the approval queue to see each one and the reason.`
                          : `${awaitingAnyone} ${awaitingAnyone === 1 ? 'bill is' : 'bills are'} still awaiting a decision, but your current grants do not let you approve or reject any of them. Open the approval queue to see each one and the reason.`
                  }
                  goodNews={!countsFailed && awaitingAnyone === 0}
                />
              ) : (
                <div className="flex flex-col">
                  {awaitingRows.map((bill) => (
                    <div key={bill.reference} className="flex items-center justify-between gap-3 py-3 border-t border-[var(--sgds-border-color-muted)] first:border-t-0">
                      <div>
                        <Link to={`/bills/${bill.reference}`} className="pt-mono font-semibold pt-ref-link">
                          {bill.reference}
                        </Link>
                        <div className="text-sm text-[var(--sgds-color-subtle)]">{bill.agencyCode} · <BillTypeBadge billType={bill.billType} /></div>
                      </div>
                      <BillStatusBadge status={bill.status} />
                    </div>
                  ))}
                </div>
              )}

              {/*
                The route to the full queue sits OUTSIDE the empty/non-empty
                branch on purpose. It used to be inside the populated one, so
                the state where it matters most — "bills are waiting, but not on
                you" — was the only state with no way through to see them. A
                refusal ships with its way out.

                ⚠ ★★ REVIEW FIX (Important 1, second pass) — `countsFailed` is
                ORed in. `awaitingAnyone > 0` cannot be trusted while the counts
                request has failed (it reads the fallback's `0`), and hiding the
                one way to check the real queue BECAUSE the figure that would have
                justified showing it is missing is the exact "refusal with no way
                out" shape this comment already warns against.
              */}
              {(countsFailed || awaitingAnyone > 0) && (
                <div className={awaitingRows.length === 0 ? 'mt-4 text-center' : 'mt-3'}>
                  <SgdsButton variant="outline" size="sm" type="button" onClick={() => navigate('/approvals')}>
                    Open the approval queue
                  </SgdsButton>
                </div>
              )}
            </div>
          </section>
        </>
      )}

      {/*
        Sortable, like every other table in the app — reusing SortableTh /
        useSort / sortParams, the exact pattern BillsListPage and AuditPage
        already use. This card stays a fixed 8-row PREVIEW ("View audit trail"
        still links to the full, un-capped page), but the sort itself hits the
        SERVER on every click (`api.audit({ limit: 8, ...sortParams(...) })`),
        the same as everywhere else this rule applies: `/api/audit` is capped,
        so sorting only the already-fetched 8 rows would silently reorder an
        arbitrary slice while looking like a real sort — e.g. clicking
        "Outcome" would just reshuffle whichever 8 events happened to be most
        recent, not surface the 8 events that actually sort first by outcome.
        Re-querying with `sort`/`dir` set gets the genuine top-8-by-column
        every time, through the same whitelisted, tiebreakered
        `AUDIT_SORT_COLUMNS` path AuditPage's full list uses — see
        `DashboardAuditSortKey` above.
      */}
      <section className="pt-card">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-lg">Recent audit activity</h2>
            {/*
              Derived from the live sort state, never static. "Last 8 events
              this scope can see" was true only while the table was unsorted —
              clicking "Outcome" re-queries the server and returns the eight
              events that sort FIRST by outcome, which is a different set. A
              subtitle the table contradicts is a false statement about the
              compliance trail, and this one sat directly above it.
            */}
            <p className="text-sm text-[var(--sgds-color-subtle)] mt-1">
              {auditPreviewSubtitle(auditSort.column, auditSort.dir, auditEvents.length)}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/*
              ★★ §12.83(f)'s self-refresh, as a control rather than as a default.
              ⚠ The sentence beneath it is not decoration: a reader who sees rows
              appear on their own must not conclude that anything on the SERVER
              runs on its own, because nothing does.
            */}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={wallLive}
                onChange={(event) => {
                  setWallLive(event.target.checked)
                }}
              />
              <span>Self-refresh</span>
            </label>
            <SgdsButton variant="outline" size="sm" type="button" onClick={() => navigate('/audit')}>
              View audit trail
            </SgdsButton>
          </div>
        </div>
        <p className="text-xs text-[var(--sgds-body-color-subtle)] -mt-2 mb-3">
          {wallLive
            ? 'Re-asking the server every 30 seconds while this tab is open. This is a BROWSER poll: nothing on the server runs on its own, and closing the tab ends it.'
            : 'Off. The rows below are the ones loaded when this page last asked; new events do not appear until you switch this on, sort a column, or reload.'}
        </p>
        <SortAnnouncement
          sort={auditSort}
          interacted={auditSorted}
          busy={audit.loading}
          columnLabels={AUDIT_COLUMN_LABELS}
          rowCount={auditEvents.length}
        />
        {audit.error !== null ? (
          <ErrorCard title="Could not load the audit trail" message={audit.error} onRetry={audit.reload} />
        ) : audit.initialLoading ? (
          <LoadingCard label="Loading audit activity" rows={4} />
        ) : auditEvents.length === 0 ? (
          <EmptyCard title="No audit events yet" message="Nothing has happened yet in this session." />
        ) : (
          // `aria-busy` while a re-sort is in flight, and the table stays
          // MOUNTED — swapping it for a skeleton would unmount the header
          // button that was just activated and drop keyboard focus to <body>.
          <div className="pt-table-wrap" aria-busy={audit.loading}>
            <table className="pt-table">
              <caption className="sr-only">
                The most recent audit events in your scope, as loaded when this page last asked
              </caption>
              <thead>
                <tr>
                  <SortableTh label="Action" sortKey="action" sort={auditSort} onSort={onAuditSort} />
                  <SortableTh label="Outcome" sortKey="outcome" sort={auditSort} onSort={onAuditSort} />
                  <SortableTh label="Actor role" sortKey="actorRole" sort={auditSort} onSort={onAuditSort} />
                  <SortableTh label="When" sortKey="occurredAt" sort={auditSort} onSort={onAuditSort} />
                </tr>
              </thead>
              <tbody>
                {auditEvents.map((event) => (
                  <tr key={event.id}>
                    <td><ActionBadge action={event.action} /></td>
                    <td>
                      {/* Badge AND grounds, the same cell the full trail uses —
                          a preview that showed only the outcome would drop the
                          one field that distinguishes a scope denial from a
                          missing record (migration 009). */}
                      <AuditOutcomeCell outcome={event.outcome} {...(event.detail !== undefined && { detail: event.detail })} />
                    </td>
                    <td><RoleBadge role={event.actorRole} /></td>
                    <td className="pt-tabular">{formatInstant(event.occurredAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/*
        ★★★ WAVE 27 — THE TREND BAND. P2C1 §12.83(k) asks for charting "for example,
        pie/column/line charts"; this product had a column chart and no line chart.

        ⚠ EVERY FIGURE IN THIS BAND IS THE SERVER'S, from ONE request, so no two
        numbers on this page can disagree about the period they cover. The tiles,
        both charts and the delta all read `series.data`.

        ⚠ THE PERIOD IS PRINTED, NOT ASSUMED. §5.3 rule 10, and on this estate it
        is load-bearing rather than ceremonial: the seeded corpus sits weeks ahead
        of the machine's clock, so "the last 30 days" would be false on every
        screen. `periodBasis` says which days these actually are.
      */}
      {/*
        ★★★ WAVE 27 (owner-directed) — the range control, above every chart it
        governs. One control for the whole band, because every figure in it comes
        from ONE request: two controls would let two numbers on one screen describe
        two different periods.
      */}
      <section className="pt-card pt-controlbar pt-no-print" aria-label="Charted period">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-semibold">Charted period</span>
          <select
            className="pt-input"
            value={days}
            onChange={(event) => setDays(Number(event.currentTarget.value))}
          >
            <option value={7}>Last 7 days of activity</option>
            <option value={14}>Last 14 days of activity</option>
            <option value={30}>Last 30 days of activity</option>
            <option value={60}>Last 60 days of activity</option>
            <option value={90}>Last 90 days of activity</option>
          </select>
        </label>
        <p className="text-sm text-[var(--sgds-color-subtle)]">
          Counted back from the latest activity your scope can see, not from today: every chart below states the
          dates it actually covers.
        </p>
      </section>

      {series.error !== null ? (
        <ErrorCard title="Could not load the trend" message={series.error} onRetry={series.reload} />
      ) : series.initialLoading ? (
        <LoadingCard label="Loading the trend" rows={4} />
      ) : (
        <>
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" aria-label="Trend">
            <TrendCard
              chipClass="pt-chip-navy"
              icon={ICONS.stack}
              label="Bills in the charted period"
              value={series.data?.totals.current ?? 0}
              delta={deltaOf(series.data?.totals.current ?? 0, series.data?.totals.previous ?? 0)}
              baseline={`vs the ${series.data?.window.days ?? 0} days before it (${(series.data?.totals.previous ?? 0).toLocaleString('en-SG')})`}
              points={series.data?.billsPerDay ?? []}
            />
            {/*
              ⚠ Decisions, not approvals. The tile counts every recorded decision —
              both approval levels AND refusals — because "how much did this office
              get through" is the operational question, and a tile that counted only
              approvals would fall when refusals rose, which reads backwards.
            */}
            <TrendCard
              chipClass="pt-chip-amber"
              icon={ICONS.check}
              label="Decisions recorded"
              value={(series.data?.transitionsPerDay ?? []).reduce((n, d) => n + d.approveL1 + d.approveL2 + d.reject, 0)}
              delta={{ text: 'across the charted period', direction: 'flat' }}
              baseline=""
              points={(series.data?.transitionsPerDay ?? []).map((d) => ({
                date: d.date,
                count: d.approveL1 + d.approveL2 + d.reject,
              }))}
            />
            <TrendCard
              chipClass="pt-chip-navy"
              icon={ICONS.cross}
              label="Refused in the charted period"
              value={(series.data?.transitionsPerDay ?? []).reduce((n, d) => n + d.reject, 0)}
              delta={{ text: 'across the charted period', direction: 'flat' }}
              baseline=""
              points={(series.data?.transitionsPerDay ?? []).map((d) => ({ date: d.date, count: d.reject }))}
            />
          </section>

          <section className="pt-card">
            <TrendChart
              title="Bills by date of medical service"
              subtitle={periodBasis(series.data, formatIsoDate)}
              dateLabel={formatIsoDate}
              series={[
                {
                  label: 'Bills',
                  slot: 1,
                  points: (series.data?.billsPerDay ?? []).map((d) => ({ date: d.date, value: d.count })),
                } satisfies TrendSeries,
              ]}
            />
          </section>

          <section className="pt-card">
            {/*
              ★★★ WAVE 27 (owner-directed, later the same day) — THIS WAS A SECOND
              LINE CHART, AND IT WAS THE WRONG FORM TWICE OVER.

              ⚠ **Wrong for the data.** Three overlaid lines answer "how did each
              move" and hide the question an operations screen is for: how much did
              this office get through on a day, and what was it made of? A stacked
              column carries the total AND the mix in one mark; three lines make a
              reader add them up by eye.

              ⚠ **Wrong for the page.** The owner's words were "still a lot of line
              chart in dashboard". Two near-identical line charts stacked one above
              the other taught a reader nothing from the difference between them —
              the second read as a redraw of the first.

              ⚠ Slots 1, 3 and 8 come from §5.2's validated set, in its own order.
              Refusal takes slot 8 (red) — the one deliberate choice, and also the
              slot furthest in hue from the two approval series, which is what the
              CVD floor wants.
            */}
            <StackedColumns
              title="Decisions recorded, by kind"
              subtitle={periodBasis(series.data, formatIsoDate)}
              labelOf={formatIsoDate}
              labels={(series.data?.transitionsPerDay ?? []).map((d) => d.date)}
              series={[
                {
                  label: 'Approved at level 1',
                  slot: 1,
                  values: (series.data?.transitionsPerDay ?? []).map((d) => d.approveL1),
                },
                {
                  label: 'Approved at level 2',
                  slot: 3,
                  values: (series.data?.transitionsPerDay ?? []).map((d) => d.approveL2),
                },
                { label: 'Refused', slot: 8, values: (series.data?.transitionsPerDay ?? []).map((d) => d.reject) },
              ]}
            />
          </section>

          {/*
            ★★★ WAVE 27 — THE COMPOSITION ROW: three forms, three different jobs,
            and not a line among them.

            ⚠ EVERY ONE IS CLICKABLE, which is the owner's other instruction: "the
            user saw it and click the charts, graph or data must be able to action
            or bring them to the page that they are able to actions on." Each link
            lands on a screen ALREADY FILTERED to the thing that was clicked — which
            is why `?billType=` and `?serviceOn=` were built in the same wave. A
            link that dropped its narrowing would be worse than no link at all.

            ★★★ AND THE MARKS THEMSELVES ARE NOW THE CONTROLS, which is the same
            instruction taken the rest of the way: "you just click on the chart, and
            on the New bill sector, if user select and click on the chart, it should
            bring user to the page that open new bill bills". Each of these cards
            used to draw a picture and then list the SAME categories underneath it
            as text links — the chart said what the shares were and the list did the
            work, side by side, saying everything twice.

            ⚠ The lists are gone and the sectors, arcs, bars and cells answer a
            press. Pointing at one names it first ("New bill — 1,525 bills — 73.6%
            of the total — Select to open these"), because an angle with no label
            is a guess and nobody should have to click to find out what they
            clicked. `mark-interaction.tsx` holds the whole argument.
          */}
          <section className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="pt-card">
              <DonutChart
                title="What the window is made of"
                subtitle={periodBasis(series.data, formatIsoDate)}
                centreLabel="bills"
                slices={(series.data?.billTypeMix ?? []).map((entry, index) => ({
                  label: BILL_TYPE_LABELS[entry.billType as Bill['billType']] ?? entry.billType,
                  slot: ([1, 2, 3] as const)[index] ?? 1,
                  value: entry.count,
                  key: entry.billType,
                }))}
                onSelect={(slice) => {
                  // ⚠ The bill TYPE, not the label: "New bill" is what a reader
                  // sees and `NEW` is what the list filters on, and the two are
                  // deliberately different words.
                  if (slice.key === undefined) return
                  navigate(`/bills?billType=${encodeURIComponent(slice.key)}`)
                }}
              />
            </div>

            <div className="pt-card">
              {/*
                ⚠ ONE RATIO, and the ring earns its place only because a reader needs
                the PART and the WHOLE at once. `ProgressRing`'s own header records
                why it is not a gauge: nothing here is a target, so there are no
                zones and no colour-as-verdict.
              */}
              <ProgressRing
                label="Approved, of everything in scope"
                part={statusCounts.data?.byStatus['APPROVED'] ?? 0}
                whole={statusCounts.data?.total ?? 0}
                caption="bills your scope can read have reached APPROVED"
                partLabel="Approved"
                onSelectPart={() => navigate('/bills?status=APPROVED')}
                /*
                 * ⚠ THE GREY ARC IS "NOT YET APPROVED", NOT "REJECTED", and the
                 * distinction is the reason the old pair of links could not simply
                 * become two arcs. The remainder is every bill that has not reached
                 * APPROVED — submitted, awaiting either level, rejected, paused — so
                 * sending it to `status=REJECTED` would have been a link whose label
                 * and destination disagreed. The bills list has no "everything
                 * except approved" filter, so this opens the unfiltered list, which
                 * is honest about being wider than the arc.
                 *
                 * ★ Rejected bills are still one press away: the ranked bars beside
                 * this ring open the refusals by reason, which is the question a
                 * reader actually has when they look for them.
                 */
                remainderLabel="Not yet approved"
                onSelectRemainder={() => navigate('/bills')}
              />
            </div>

            <div className="pt-card">
              {/*
                ⚠ RANKED BARS, not a second donut: this compares magnitudes across
                NAMED categories, which is the one job an angle is worst at. Each bar
                links to the records behind it — a drill-down that already worked,
                now drawn rather than listed.
              */}
              <RankedBars
                title="Why records were refused"
                subtitle="Across every delivery your scope can read, not only the charted window."
                bars={(alerts.data?.recordReasons ?? []).map((reason) => ({
                  label: reason.reasonKind,
                  value: reason.count,
                  href: `/records?outcome=REJECTED&reasonKind=${encodeURIComponent(reason.reasonKind)}`,
                }))}
                renderLink={(bar) => <Link to={bar.href ?? '/records'}>{bar.label}</Link>}
                onSelect={(bar) => navigate(bar.href ?? '/records')}
              />
            </div>
          </section>

          <section className="pt-card">
            {/*
              ★★★ WAVE 27 — DENSITY, which none of the other forms can show.

              A line over thirty days shows the shape of the month. It does NOT show
              that the shape is a WEEKLY one. Laid out as weeks down and weekdays
              across, five dense columns and two empty ones are the picture — and on
              this estate that is a real property rather than a decoration, because
              the corpus is built over BUSINESS days and a reader who cannot see the
              rhythm concludes the product lost a third of its data.
            */}
            <ActivityHeatmap
              title="When the work actually happened"
              subtitle={periodBasis(series.data, formatIsoDate)}
              dateLabel={formatIsoDate}
              days={series.data?.billsPerDay ?? []}
              /*
               * ★★★ EVERY day, not just the last one. This replaced a single link
               * reading "Open the bills for 27 Aug 2026" — thirty cells were drawn
               * and exactly one of them was reachable, which is the shape that
               * teaches a reader the picture is decoration.
               */
              onSelectDay={(day) => navigate(`/bills?serviceOn=${encodeURIComponent(day.date)}`)}
            />
          </section>
        </>
      )}


      <section className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/*
          ⚠ NOT BUILT, AND THE REASON IS STRONGER THAN "no clause defines it".
          Measured against the estate on 2026-08-26: every APPROVED bill carries a
          non-null `approved_by` and `app.bill_transition` holds only APPROVE_L1,
          APPROVE_L2 and REJECT — there is no automatic approval path in this
          product at all. A tile whose numerator is structurally empty would read
          0% forever, and 0% reads as a performance failure rather than as an
          absent capability. Redefining the measure is an owner decision
          (`docs/HANDOFF.md` §W27.0), not a rewording to slip in here.
        */}
        <AwaitingCard
          title="Straight-through processing rate"
          note="Would show the share of bills pro-rated without a manual approval step. Every approval in this release is a decision an officer takes (there is no automatic path for a bill to take), so the measure has nothing to count yet, and PSD has not defined it."
          availableWhen="an automatic approval path is introduced and PSD settles what the measure counts"
        />
        {/*
          ⚠ NOT BUILT, AND "SFTP vs API" MUST NOT BE BUILT AS WORDED — neither
          transport exists in this deployment. The SFTP / Cloud File Transfer
          endpoint is boundary item B-3 in `docs/delivery/DEPLOYMENT-COMPLETION.md`
          and no online submission API is built, so a split between them would be
          two numbers about nothing. What IS derivable is a different axis
          altogether — arrived in a delivery vs created another way — and putting
          that behind this card's current label would answer a question nobody
          asked while still not answering this one.
        */}
        <AwaitingCard
          title="Submission channel throughput"
          note="Would compare SFTP batch against online/API volume. Neither transport is deployed in this environment (the file-transfer endpoint is a named deployment boundary), and no bill or delivery record carries a channel field to split on."
          availableWhen="a file-transfer endpoint or a submission API is deployed and deliveries record which one they arrived on"
        />
        <InterfaceAlertsCard alerts={alerts} />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {isRecovery ? (
          <AwaitingCard
            title="Recovery agency settlements"
            note="Your role carries recovery visibility. This release produces no settlement artefact addressed to a recovery agency: the one artefact addressed to yours is the re-proration alert, which the file exchange screen already lists."
            availableWhen="settlement artefacts are produced for recovery agencies and your access covers the agency they are addressed to"
          />
        ) : (
          <NoPermissionCard requiredRole={ROLE_LABELS.RECOVERY_USER} />
        )}
      </section>
    </div>
  )
}
